import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import type { PermissionGroup } from 'src/apis/authorization-service/types';
import type { OrganisationSettingsRequest } from 'src/apis/authorization-service/use-organisation-settings';
import type { PermissionGroupsRequest } from 'src/apis/authorization-service/use-permission-groups';
import { useUpdateOrganisationSettings } from 'src/apis/authorization-service/use-update-organisation-settings';
import { Button } from 'src/components/buttons-and-actions/button';
import { Toggle } from 'src/components/buttons-and-actions/switch';
import { Card } from 'src/components/data-display/card';
import { Stack } from 'src/components/layout/stack';
import { TourTip } from 'src/components/overlay/tour';
import { Heading } from 'src/components/text/heading';
import { isServerError, type Successful } from 'src/hooks/use-axios';
import { useToast } from 'src/hooks/use-toasts';
import type { TourTipProps } from 'src/pages/settings/permission-groups/overview/page';
import { Colors } from 'src/styles';

type OrganisationSettingsCardProps = {
  organisationSettingsRequest: Successful<OrganisationSettingsRequest>;
  role: String;
  customGroups: PermissionGroup[];
  permissionGroups: Successful<PermissionGroupsRequest>;
  tourTipProps: TourTipProps;
};

export const OrganisationSettingsCard: FC<OrganisationSettingsCardProps> = ({
  organisationSettingsRequest,
  role,
  customGroups,
  permissionGroups,
  tourTipProps,
}) => {
  const {
    tourSteps,
    currentStepIndex,
    maxStepIndex,
    setCurrentStepIndex,
    isLast,
  } = tourTipProps;
  const toast = useToast();
  const showToast = () => {
    toast({
      type: 'warning',
      children: (
        <div data-testid="delete-all-custom-groups-before-turning-to-safe-mode-toast">
          Please delete your Custom Groups before selecting the Safe Mode.
        </div>
      ),
    });
  };

  const updateOrganisationSettings = useUpdateOrganisationSettings((error) => {
    if (isServerError(error, 400)) {
      showToast();
      permissionGroups.refresh?.();
      return;
    }
    throw error;
  });

  const startTour = () => {
    setCurrentStepIndex(0);
  };

  const prevIsAdvanceModeEnabledValue = useRef(
    organisationSettingsRequest.response.data.isAdvanceModeEnabled
  );
  useEffect(() => {
    const isAdvanceModeEnabled =
      organisationSettingsRequest.response.data.isAdvanceModeEnabled;

    if (
      prevIsAdvanceModeEnabledValue.current !== isAdvanceModeEnabled &&
      isAdvanceModeEnabled
    ) {
      startTour();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationSettingsRequest.response.data.isAdvanceModeEnabled]);

  const handleToggleChange = () => {
    if (customGroups.length > 0) {
      showToast();
      return;
    }
    const isAdvanceModeEnabled =
      !organisationSettingsRequest.response.data.isAdvanceModeEnabled;
    updateOrganisationSettings.execute({
      organisationSettings: organisationSettingsRequest,
      data: {
        isAdvanceModeEnabled,
      },
    });
  };

  useEffect(() => {
    if (updateOrganisationSettings.response) {
      organisationSettingsRequest.refresh?.();
      permissionGroups.refresh?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateOrganisationSettings.response]);

  return (
    <Card data-testid="organisation-settings-card">
      <Stack gap={1.5}>
        <Stack flow="column" alignItems="start">
          <Stack>
            <Heading mode="card">Permissions Tour</Heading>
            Start the tour to learn more about the Permission Groups settings
            and usage. You can start the tour whenever you like, and skip any
            steps that don’t seem relevant to you. You can turn it off and
            re-visit it later if you wish.
          </Stack>
          <Stack justifyContent="end">
            <Button
              mode="secondary"
              size="small"
              onClick={() => {
                startTour();
              }}
            >
              Start Tour
            </Button>
          </Stack>
        </Stack>

        <TourTip
          step={{
            ...tourSteps[currentStepIndex],
            active: currentStepIndex < maxStepIndex,
            isLast,
            next: () => setCurrentStepIndex((prev) => prev + 1),
            exit: () => setCurrentStepIndex(tourSteps.length),
          }}
        >
          <Stack flow="column" alignItems="start">
            <Stack>
              <Heading mode="card">Safe Mode</Heading>
              {organisationSettingsRequest.response.data
                .isAdvanceModeEnabled ? (
                <>
                  <b style={{ color: Colors.inactive }}>Beta Feature</b>
                  The Safe Mode is off, you are now in Advanced Mode. This mode
                  allows you to create Custom Permission Groups and assign your
                  users to them. Please note, however, that this is a Beta
                  Feature. So, while PRISMA updates its functionalities, some
                  features might become inaccessible to users assigned only to
                  Custom Groups. As such, we strongly recommend using our
                  Standard {role} User Group in Safe Mode. If you wish to use
                  Custom Groups, always double check whether your permissions
                  work as expected.
                </>
              ) : (
                <>
                  <b style={{ color: Colors.brandSkyBlue }}>Recommended</b>
                  Safe Mode will give you control over all essential features on
                  the platform through our Standard Groups.
                </>
              )}
            </Stack>
            <Stack justifyContent="end">
              <Toggle
                data-testid="organisation-settings-toggle"
                label=""
                isChecked={
                  !organisationSettingsRequest.response.data
                    .isAdvanceModeEnabled
                }
                onChange={handleToggleChange}
                loading={updateOrganisationSettings.pending}
                labels={{ checked: 'On', unchecked: 'Off' }}
              />
            </Stack>
          </Stack>
        </TourTip>
      </Stack>
    </Card>
  );
};
